import React from 'react'
import { NavLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import logo from '@/assets/logo.png'
import { Home, LogOut, X, Ticket, Wallet, ShoppingBag, Award, CreditCard } from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { redirectToSSOLogin } from '@/lib/sso'

interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const navItems = [
  { label: 'Tổng quan', icon: Home, path: '/', end: true },
  { label: 'Quản lý Giftcode', icon: Ticket, path: '/gift-codes', end: false },
  { label: 'Đơn hàng giới thiệu', icon: ShoppingBag, path: '/referrals', end: false },
  { label: 'Thưởng doanh số', icon: Award, path: '/monthly-bonus', end: false },
  { label: 'Nhận tiền giới thiệu', icon: Wallet, path: '/payouts', end: false },
]

export const AppSidebar: React.FC<AppSidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth()

  const handleLogout = async () => {
    await logout()
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:relative lg:translate-x-0 lg:z-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="px-4 border-b border-gray-200 flex items-center justify-between h-[60px]">
          <a href="https://hocnhe.com" className="flex items-center gap-2 text-left">
            <img src={logo} alt="Học Nhé" className="w-8 h-8 object-contain" />
            <div className="flex flex-col">
              <span className="text-[15px] font-black tracking-tight leading-none text-[#0068FF]">
                Học Nhé
              </span>
              <span className="text-[9px] text-[#E65F00] font-extrabold tracking-wider mt-0.5 uppercase">
                Học cùng nhau
              </span>
            </div>
          </a>
          <button onClick={onClose} className="lg:hidden p-1 rounded-md hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50/50 text-[#0068FF]'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-gray-200">
          {user && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt="Avatar"
                  className="w-8 h-8 rounded-full object-cover border border-[#E65F00]"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center border border-[#E65F00]">
                  <span className="text-xs font-bold text-white">
                    {(user.display_name || user.email).charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.display_name || user.email}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-red-650 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            Đăng xuất
          </button>
        </div>
      </aside>
    </>
  )
}
